import { useMutation, gql } from '@apollo/client'
import { Field, Form, Formik } from 'formik'
import { useRouter } from 'next/router'
import React from 'react'
import styled from 'styled-components'
import * as Yup from 'yup'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { Box, Container, Flex } from 'components/Layout'
import Link from 'components/Link'
import Logo from 'components/Logo'
import PasswordInput from 'components/PasswordInput'
import { Toast } from 'components/Toast'
import { H4, Text } from 'components/Typography'
import { ROUTE } from 'constants/routes'
import { withGA4Provider } from 'hoc/withGoogleAnalytics'
import { GA4_PAGE_AREAS, GA4_SIGN_IN_AREAS } from 'providers/googleAnalytics'
import { COLOR, SPACE } from 'Theme'
import { passwordValidationSchema } from 'utils/validation'

const Wrapper = styled(Box)`
  background-color: ${COLOR.GRAYSCALE_6};
`

const SEND_RESET_PASSWORD_EMAIL_MUTATION = gql`
  mutation sendResetPasswordEmail($email: String!) {
    sendResetPasswordEmail(email: $email)
  }
`

const RESET_PASSWORD_MUTATION = gql`
  mutation resetPassword($input: ResetPasswordInput!) {
    resetPassword(input: $input)
  }
`

const emailValidationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').trim().label('Email').required(),
})

const ResetPasswordPage = () => {
  const router = useRouter()
  const { resetPasswordToken } = router.query
  const [
    executeSendResetPasswordEmail,
    { loading: isSendResetPasswordLoading },
  ] = useMutation(SEND_RESET_PASSWORD_EMAIL_MUTATION, {
    context: { isPublic: true },
  })
  const [executeResetPassword, { loading: isResetPasswordLoading }] =
    useMutation(RESET_PASSWORD_MUTATION, { context: { isPublic: true } })

  return (
    <Wrapper minHeight="100vh" pb={{ MOBILE: SPACE.PX_60 }}>
      <Container.Small
        flexDirection="column"
        alignItems="center"
        pl={{ MOBILE: SPACE.PX_15, TABLET: 0 }}
        pr={{ MOBILE: SPACE.PX_15, TABLET: 0 }}
      >
        <Box m={`${SPACE.PX_20} 0`}>
          <Link href={ROUTE.FRONT_PAGE()} aria-label="home">
            <Logo />
          </Link>
        </Box>
        <H4 mb={SPACE.PX_30}>Reset Your Password</H4>
        <Flex flexDirection="column" width="100%">
          {resetPasswordToken ? (
            <Formik
              initialValues={{
                password: '',
                confirmPassword: '',
              }}
              validationSchema={passwordValidationSchema}
              onSubmit={async ({ password }) => {
                const response = await executeResetPassword({
                  variables: {
                    input: {
                      resetPasswordToken,
                      password,
                    },
                  },
                })
                if (!response.errors) {
                  Toast.success(
                    'Password changed successfully. Please sign in with your new password.'
                  )
                  router.push(
                    ROUTE.SIGN_IN({
                      fromUrl: GA4_SIGN_IN_AREAS.RESET_PASSWORD,
                    })
                  )
                }
              }}
            >
              <Form>
                <Flex flexDirection="column">
                  <Field name="password">
                    {({ field, meta }) => (
                      <PasswordInput
                        label="New password"
                        hint={
                          meta.touched && !meta.error
                            ? 'Your password is strong enough'
                            : meta.error
                        }
                        hasHint
                        autocomplete={false}
                        isDisabled={isResetPasswordLoading}
                        isError={meta.touched && meta.error !== undefined}
                        {...field}
                      />
                    )}
                  </Field>
                  <Field name="confirmPassword">
                    {({ field, meta }) => (
                      <PasswordInput
                        label="Confirm new password"
                        autocomplete={false}
                        isDisabled={isResetPasswordLoading}
                        isError={meta.touched && meta.error !== undefined}
                        hint={meta.error}
                        hasHint
                        {...field}
                      />
                    )}
                  </Field>
                  <Box mt={SPACE.PX_5}>
                    <Button type="submit" isLoading={isResetPasswordLoading}>
                      Reset Password
                    </Button>
                  </Box>
                </Flex>
              </Form>
            </Formik>
          ) : (
            <>
              <Text
                p={`0 ${SPACE.PX_15} ${SPACE.PX_20}`}
                textAlign="center"
                color={COLOR.GRAYSCALE_2}
                justifyContent="center"
              >
                Enter your email address and we&apos;ll send you a password
                reset link.
              </Text>
              <Formik
                initialValues={{ email: '' }}
                validationSchema={emailValidationSchema}
                onSubmit={async ({ email }) => {
                  const response = await executeSendResetPasswordEmail({
                    variables: {
                      email: email.trim(),
                    },
                  })
                  if (!response.errors) {
                    Toast.success(
                      'You will receive a password reset email soon. Follow the link in the email to reset your password.'
                    )
                  }
                }}
              >
                <Form>
                  <Field name="email">
                    {({ field, meta }) => (
                      <Input
                        label="Email"
                        isDisabled={isSendResetPasswordLoading}
                        isError={meta.touched && meta.error !== undefined}
                        hint={meta.touched ? meta.error : ''}
                        hasHint
                        {...field}
                      />
                    )}
                  </Field>
                  <Button isLoading={isSendResetPasswordLoading} type="submit">
                    Reset password
                  </Button>
                </Form>
              </Formik>
            </>
          )}
        </Flex>
      </Container.Small>
    </Wrapper>
  )
}

export default withGA4Provider({
  page_area: GA4_PAGE_AREAS.RESET_PASSWORD,
})(ResetPasswordPage)
